import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  PlaneTypes,
} from '@Components/BigBrowse/Model/ModelTypes';
import { getThreeUpCard } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/threeUp/helpers/GetThreeUpCard';
import { Assets } from '@Utils/AssetsStore';
import { Dimensions } from 'react-native';

import type {
  BrandConcessionContentDM,
  ThreeUpImage,
  ThreeUpStyles,
  ThreeUpView,
} from '../../common/MultiSectionView.types';
import { getColumnWidth } from '../../merchandiser/helpers/MerchandiserCommon';

const FIGMA_BOX_SIZE: PlaneSize = { width: 1440, height: 860 };
const FIGMA_IMAGES_GROUP_SIZE: PlaneSize = { width: 532, height: 612 };

const BOX_ASPECT_RATIO = FIGMA_BOX_SIZE.width / FIGMA_BOX_SIZE.height;
const IMAGES_GROUP_ASPECT_RATIO =
  FIGMA_IMAGES_GROUP_SIZE.width / FIGMA_IMAGES_GROUP_SIZE.height;

export const layoutThreeUpLandscape = ({
  section,
  layoutTable,
}: {
  section: ThreeUpView;
  layoutTable: GridCard<PlaneTypes>[][];
}) => {
  const styles = getBoxLayoutLandScapeThreeUp(section);

  layoutTable.forEach((column, index) => {
    column.push(
      index === 0
        ? getThreeUpCard(section, styles)
        : getGap({ width: getColumnWidth(), height: styles!.boxSize.height })
    );
  });
};

export const getBoxLayoutLandScapeThreeUp = (
  section: ThreeUpView
): ThreeUpStyles => {
  const contentPosition = { x: 0, y: 0 };
  const { width, height } = Dimensions.get('window');

  const screenAspectRatio = width / height;
  // contentBox sizes based on aspect ratio of screen
  const contentBoxSize =
    screenAspectRatio >= BOX_ASPECT_RATIO
      ? { height, width: height * BOX_ASPECT_RATIO }
      : {
          width,
          height: width / BOX_ASPECT_RATIO,
        };

  const boxSize =
    screenAspectRatio >= BOX_ASPECT_RATIO ? { width, height } : contentBoxSize;

  const textPart = getTextPart({ section, contentBoxSize });
  const imagesPart = getImagesPart({ section, contentBoxSize });

  return {
    boxSize,
    contentBoxSize,
    contentPosition,
    textPart,
    imagesPart,
    layout: 'landscape',
  };
};

const getTextPart = ({
  section,
  contentBoxSize,
}: {
  section: ThreeUpView;
  contentBoxSize: PlaneSize;
}): ThreeUpStyles['textPart'] => {
  const size = {
    height: contentBoxSize.height,
    width: contentBoxSize.width / 2,
  };

  const textContentsContainer: PlaneSize = {
    width: (contentBoxSize.width * 476) / 1440,
    height: contentBoxSize.height,
  };
  const paragraphs = getParagraphs({
    section,
    wrapperSize: textContentsContainer,
  });

  const moreCta: ThreeUpStyles['textPart']['moreCta'] = {
    textProps: {
      text: section.dtoData.linkButtonTitle,
      fontSize: 16,
      fontAsset: Assets.Font_Gilroy_SemiBold,
      lineHeight: 19 / 16,
      letterSpacing: 0.03,
    },
    boxSize: { height: 47, width: 168 },
  };

  return {
    size,
    position: { y: 0, x: -Math.round(size.width / 2) },
    textContentsContainer,
    paragraphs,
    moreCta,
  };
};

const getImagesPart = ({
  section,
  contentBoxSize,
}: {
  section: ThreeUpView;
  contentBoxSize: PlaneSize;
}): ThreeUpStyles['imagesPart'] => {
  const size = {
    height: contentBoxSize.height,
    width: contentBoxSize.width / 2,
  };

  const groupBoxSize = getImagesGroupBoxSize(size);

  return {
    size,
    position: { y: 0, x: Math.round(size.width / 2) },
    imagesGroupBox: {
      size: groupBoxSize,
    },
    images: getImages(section, groupBoxSize),
  };
};

const getParagraphs = ({
  section,
  wrapperSize,
}: {
  section: ThreeUpView;
  wrapperSize: PlaneSize;
}): CardTextProps[] => {
  const removedDoubledLines = section.dtoData.text.replace(/\n\n+/g, '\n');

  const paragraphArray = removedDoubledLines.split('\n');

  return paragraphArray.map((paragraph, index) => {
    const isBold = index === 0 && paragraphArray.length > 1;
    const fontFamily = isBold
      ? Assets.Font_Gilroy_Bold
      : Assets.Font_Gilroy_Medium;

    const props: CardTextProps = {
      text: paragraph,
      fontAsset: fontFamily,
      fontSize: 18,
      maxWidth: wrapperSize.width,
      color: section.dtoData.fontColour ?? 'black',
      lineHeight: 22 / 18,
      letterSpacing: 0.03,
    };

    return props;
  });
};
const getImagesGroupBoxSize = (parentBox: PlaneSize): PlaneSize => {
  const parentAspectRatio = parentBox.width / parentBox.height;

  if (IMAGES_GROUP_ASPECT_RATIO <= parentAspectRatio) {
    const height =
      parentBox.height *
      (FIGMA_IMAGES_GROUP_SIZE.height / FIGMA_BOX_SIZE.height);
    const width = height * IMAGES_GROUP_ASPECT_RATIO;

    return { width: Math.round(width), height: Math.round(height) };
  }

  const width =
    parentBox.width *
    (FIGMA_IMAGES_GROUP_SIZE.width / (FIGMA_BOX_SIZE.width / 2));
  const height = width / IMAGES_GROUP_ASPECT_RATIO;

  return { width: Math.round(width), height: Math.round(height) };
};

const getImages = (
  section: ThreeUpView,
  groupBoxSize: PlaneSize
): ThreeUpImage[] =>
  section.dtoData.products.slice(0, 3).map((product, index) =>
    getThreeUpImage({
      product,
      definition: threeUpDefinitions[index],
      groupBoxSize,
    })
  );

type ThreeUpImageDefinition = [number, number, number]; // [size, top, left]
const threeUpDefinitions: ThreeUpImageDefinition[] = [
  [353, 259, 21],
  [257, 85, 275],
  [179, 0, 0],
];

const getThreeUpImage = ({
  product,
  definition,
  groupBoxSize,
}: {
  // @ts-expect-error
  product: BrandConcessionContentDM['header']['products'][0];
  definition: ThreeUpImageDefinition;
  groupBoxSize: PlaneSize;
}): ThreeUpImage => {
  const [defSize, top, left] = definition;

  const size = groupBoxSize.width * (defSize / FIGMA_IMAGES_GROUP_SIZE.width);
  const y = // center of image based on center of group and its top distance from the top of the group
    groupBoxSize.height / 2 -
    groupBoxSize.height * (top / FIGMA_IMAGES_GROUP_SIZE.height) -
    size / 2;

  const x =
    -groupBoxSize.width / 2 +
    groupBoxSize.width * (left / FIGMA_IMAGES_GROUP_SIZE.width) +
    size / 2;

  return { size, position: { x, y }, dto: product };
};
